/**
 * WAR ROOM REPLAYS — Public listing of shareable debates
 * Anyone can browse and watch past AI god debates.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Eye, ArrowRight, Loader2, Sparkles, Users, Play } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GOD_CONFIG = {
  calliope: { name: 'Calliope', emoji: '🎭', gradient: 'from-pink-500 to-purple-600' },
  eris:     { name: 'Eris',     emoji: '⚔️',  gradient: 'from-red-600 to-pink-600' },
  plutus:   { name: 'Plutus',   emoji: '💰',  gradient: 'from-green-500 to-emerald-600' },
  hestia:   { name: 'Hestia',   emoji: '🏠',  gradient: 'from-orange-400 to-amber-500' },
  themis:   { name: 'Themis',   emoji: '⚖️',  gradient: 'from-blue-600 to-cyan-600' },
  nike:     { name: 'Nike',     emoji: '🏆',  gradient: 'from-yellow-400 to-orange-500' },
  metis:    { name: 'Metis',    emoji: '🧠',  gradient: 'from-indigo-600 to-purple-700' },
  athena:   { name: 'Athena',   emoji: '🦉',  gradient: 'from-amber-500 to-orange-600' },
  hermes:   { name: 'Hermes',   emoji: '👟',  gradient: 'from-orange-500 to-red-600' },
  apollo:   { name: 'Apollo',   emoji: '☀️',  gradient: 'from-yellow-500 to-orange-500' },
  dionysus: { name: 'Dionysus', emoji: '🍷',  gradient: 'from-purple-600 to-red-600' },
  ares:     { name: 'Ares',     emoji: '🗡️',  gradient: 'from-red-600 to-rose-700' },
};

const DebateCard = ({ debate }) => {
  const gods = (debate.gods || []).slice(0, 4);
  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return 'just now';
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <Link
      to={`/living-room/${debate.share_id}`}
      className="group block bg-gray-900/70 border border-gray-700/50 hover:border-purple-500/40 rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10 hover:-translate-y-0.5"
      data-testid={`replay-card-${debate.share_id}`}
    >
      {/* Top gradient strip with god avatars */}
      <div className="h-2 bg-gradient-to-r from-purple-600 via-red-500 to-amber-500 opacity-60 group-hover:opacity-100 transition-opacity" />

      <div className="p-5">
        {/* God avatars */}
        <div className="flex items-center gap-1 mb-3">
          <div className="flex -space-x-2 mr-2">
            {gods.map((id) => {
              const god = GOD_CONFIG[id];
              if (!god) return null;
              return (
                <div key={id} className={`w-7 h-7 rounded-full bg-gradient-to-br ${god.gradient} flex items-center justify-center border-2 border-gray-900 text-xs shadow`} title={god.name}>
                  {god.emoji}
                </div>
              );
            })}
          </div>
          <span className="text-[10px] text-gray-500">{debate.gods?.length || 0} gods debating</span>
          <div className="ml-auto flex items-center gap-1 text-[10px] text-gray-600">
            <Eye className="w-3 h-3" /> {debate.views || 0}
          </div>
        </div>

        {/* Topic */}
        <p className="text-sm text-gray-200 font-medium leading-snug mb-3 line-clamp-2 group-hover:text-white transition-colors">
          "{debate.topic}"
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-gray-600">{timeAgo(debate.created_at)}</span>
          <div className="flex items-center gap-1 text-[10px] text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity">
            <Play className="w-3 h-3" /> Watch replay
          </div>
        </div>
      </div>
    </Link>
  );
};

const WarRoomReplaysPage = () => {
  const [debates, setDebates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/war-council/living-room/public`);
        const data = await res.json();
        setDebates(data.debates || []);
      } catch (e) {
        console.error('Replays fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-[#030305]">
      {/* Background ambient */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-purple-600/4 rounded-full blur-[200px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-red-600/4 rounded-full blur-[200px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-10">

        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-semibold">iSupplyAI</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-medium hover:from-purple-500 hover:to-indigo-500 transition-all"
            data-testid="start-own-debate-btn"
          >
            Start Your Debate <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-medium mb-4">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            Live Debates
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4">
            The War Room <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-red-400">Replays</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Watch 12 AI gods argue real business strategy. Every debate is unique, brutally honest, and data-backed.
          </p>
          <div className="flex items-center justify-center gap-6 mt-6 text-sm text-gray-500">
            <div className="flex items-center gap-1.5"><Flame className="w-4 h-4 text-orange-400" /> 12 AI gods per debate</div>
            <div className="flex items-center gap-1.5"><Users className="w-4 h-4 text-purple-400" /> Real strategy arguments</div>
            <div className="flex items-center gap-1.5"><Eye className="w-4 h-4 text-blue-400" /> No login required</div>
          </div>
        </div>

        {/* Debates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Loading debates from the pantheon...</p>
            </div>
          </div>
        ) : debates.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-16 h-16 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mx-auto mb-4">
              <Flame className="w-8 h-8 text-purple-400" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">No Replays Yet</h3>
            <p className="text-gray-500 text-sm mb-6 max-w-xs mx-auto">
              Be the first to run a debate and share it with the world.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-medium hover:opacity-90 transition-opacity">
              Start a Debate <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="replays-grid">
            {debates.map((d) => <DebateCard key={d.share_id} debate={d} />)}
          </div>
        )}

        {/* CTA */}
        {!loading && debates.length > 0 && (
          <div className="mt-16 rounded-2xl border border-purple-500/20 bg-gradient-to-r from-purple-900/20 via-gray-900/50 to-indigo-900/20 p-10 text-center">
            <h3 className="text-2xl font-black text-white mb-3">Ready for the gods to debate YOUR strategy?</h3>
            <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
              12 AI gods with distinct personalities, personalized to your business data. They remember. They disagree. They evolve.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-gradient-to-r from-purple-600 to-red-600 text-white font-semibold text-sm hover:from-purple-500 hover:to-red-500 transition-all shadow-lg shadow-purple-500/20"
              data-testid="replays-cta-btn"
            >
              Enter the Pantheon <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-[10px] text-gray-600 mt-3">Free to start. No credit card required.</p>
          </div>
        )}

        <div className="text-center mt-10 text-[10px] text-gray-700">
          Powered by iSupplyAI — 12 AI Gods. One Strategy.
        </div>
      </div>
    </div>
  );
};

export default WarRoomReplaysPage;
